import { initializeApp, getApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, isSupported as isAnalyticsSupported, Analytics, logEvent } from 'firebase/analytics';
import { getDatabase, Database } from 'firebase/database';
import { getAuth, browserLocalPersistence, setPersistence, Auth } from 'firebase/auth';
import { getPerformance, FirebasePerformance } from 'firebase/performance';

// -----------------------------------------------------------------------------
// Fail-fast configuration check
// -----------------------------------------------------------------------------
// A missing/typo'd REACT_APP_FIREBASE_* variable used to surface as a cryptic
// Firebase error ("Firebase: No Firebase App '[DEFAULT]' has been created")
// or a blank map/database at runtime. We now validate everything up front and
// report ONE actionable error that names the variables (never their values).
//
// The check treats both unset variables and obvious placeholders
// (e.g. `your_api_key_here`) as missing, so a fresh `.env.example` copy is
// caught immediately. See `.env.example` for the setup instructions.
// -----------------------------------------------------------------------------

const REQUIRED_FIREBASE_ENV = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_DATABASE_URL',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID'
] as const;

// Analytics-only: the app still works without it (measurement is disabled).
const OPTIONAL_FIREBASE_ENV = [
  'REACT_APP_FIREBASE_MEASUREMENT_ID'
] as const;

const isUnset = (value: string | undefined): boolean => {
  const v = (value ?? '').trim();
  // Empty, or an obvious template placeholder left unchanged.
  return v === '' || /^(your[-_].*|xxx+|changeme|change[-_]?me|todo|<.*>)$/i.test(v);
};

const missingRequired = REQUIRED_FIREBASE_ENV.filter((name) => isUnset(process.env[name]));
const missingOptional = OPTIONAL_FIREBASE_ENV.filter((name) => isUnset(process.env[name]));

if (missingRequired.length > 0) {
  // Names only — never log the values themselves.
  console.error(
    [
      '[firebase/config] Incomplete Firebase configuration — the app cannot start.',
      `Missing or placeholder variable(s): ${missingRequired.join(', ')}`,
      '',
      'How to fix:',
      '  1. Copy `.env.example` to `.env` in the project root.',
      '  2. Fill in every REACT_APP_FIREBASE_* value (see Firebase console',
      '     → Project settings → Your apps → SDK setup).',
      '  3. Restart `npm start` (or rebuild) — CRA bakes env values in at',
      '     build time, so an old build keeps its old values.',
      '  Keep `.env` out of git; commit only `.env.example`.'
    ].join('\n')
  );
  throw new Error(
    `DJOCO Firebase config incomplete: missing ${missingRequired.join(', ')}. ` +
      'See the console error above for fix steps (copy .env.example -> .env).'
  );
}

if (missingOptional.length > 0) {
  console.warn(
    `[firebase/config] Optional variable(s) not set: ${missingOptional.join(', ')} ` +
      '— Firebase Analytics reporting will be unavailable. This does not block the app.'
  );
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

interface FirebaseServices {
  app: FirebaseApp;
  database: Database;
  auth: Auth;
}

// Initialize Firebase with error handling
const initializeFirebase = (): FirebaseServices => {
  try {
    // Check if app is already initialized
    let app;
    try {
      app = initializeApp(firebaseConfig);
    } catch (error) {
      if (error instanceof Error && error.message.includes('already exists')) {
        console.debug('Firebase app already initialized');
        app = getApp();
      } else {
        throw error;
      }
    }

    const database = getDatabase(app);
    const auth = getAuth(app);

    // Set up auth persistence
    setPersistence(auth, browserLocalPersistence).catch(() => {
      // Silently fail persistence setup
    });

    return { app, database, auth };
  } catch (error) {
    console.error('Firebase initialization error:', error);
    throw error;
  }
};

// Initialize services
const { app, database, auth } = initializeFirebase();

// Initialize analytics conditionally
let analytics: Analytics | null = null;
// Firebase Performance Monitoring instance. Consumers: none today (the export
// exists for future use); typed explicitly so `tsc --noEmit` stays clean
// instead of relying on the implicit `any` of `let x = null`.
let performance: FirebasePerformance | null = null;

// Helper function to check if tracking is blocked
const isTrackingBlocked = () => {
  try {
    return Boolean(
      window.navigator.webdriver || 
      window.Cypress || 
      window.localStorage.getItem('blockAnalytics') === 'true' ||
      (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__ ||
      /Firefox/.test(navigator.userAgent) // Firefox often blocks tracking
    );
  } catch {
    return true;
  }
};

// Only initialize analytics and performance in production
if (process.env.NODE_ENV === 'production' && !isTrackingBlocked()) {
  // Initialize analytics without retries
  const initAnalytics = async () => {
    try {
      const supported = await Promise.race([
        isAnalyticsSupported(),
        new Promise((_, reject) => setTimeout(() => reject(), 2000)) // 2s timeout
      ]);
      
      if (supported) {
        analytics = getAnalytics(app);
      }
    } catch {
      // Silently fail analytics initialization
    }
  };

  // Initialize performance without retries
  const initPerformance = async () => {
    try {
      performance = getPerformance(app);
    } catch {
      // Silently fail performance initialization
    }
  };

  // Initialize both services without waiting
  initAnalytics();
  initPerformance();
}

// Export a safe analytics logger
export const logAnalyticsEvent = (eventName: string, eventParams: Record<string, any>) => {
  try {
    if (analytics) {
      logEvent(analytics, eventName, eventParams);
    }
  } catch {
    // Silently fail analytics logging
  }
};

// Export initialized services
export {
  app,
  database,
  auth,
  analytics,
  performance
};